import { logger } from '@springtree/eva-sdk-core-logger';
import { settings } from '@springtree/eva-sdk-core-settings';
import { BehaviorSubject } from 'rxjs';
import { SETTINGS_GROUP_NAME } from '../settings.js';
import { Core } from '@springtree/eva-services-core';
export class EvaContextUser {
    _status$ = new BehaviorSubject('LOGGED-OUT');
    get status$() { return this._status$.asObservable(); }
    get status() { return this._status$.getValue(); }
    _authenticationToken;
    get authenticationToken() { return this._authenticationToken; }
    _refreshToken;
    get refreshToken() { return this._refreshToken; }
    _applicationToken;
    get applicationToken() { return this._applicationToken; }
    _endpoint;
    _settingsSubscription;
    constructor({ applicationToken, authenticationToken, endpoint, refreshToken, }) {
        this._endpoint = endpoint;
        this.updateTokens({ applicationToken, authenticationToken, refreshToken });
    }
    // Pure JavaScript JWT payload decode (without signature validation)
    // We will use this to get our hands on the expiration time
    //
    // Source: https://stackoverflow.com/a/38552302
    //
    parseJwtPayload(token) {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        const tokenData = JSON.parse(jsonPayload);
        return tokenData;
    }
    // Return the current JWT expiration time which is always a JavaScript timestamp in seconds since epoch
    // Will return undefined if the current token is not a JWT token.
    // The has_expired function will always check against the current time.
    //
    getJwtExpiration() {
        const token = this._authenticationToken;
        if (token && token.length > 64)
            try {
                const jwtPayload = this.parseJwtPayload(token);
                const exp = jwtPayload.exp;
                if (exp) {
                    return { expires_at: exp, has_expired: () => Math.round(new Date().valueOf() / 1000) > exp };
                }
            }
            catch {
                // NOOP
            }
    }
    // Check the `exp` value embedded in the JWT token to see if it has expired and request a new token
    // using the JWT refresh token if needed. Will rethrow the service response error if refreshing the token fails.
    //
    async checkAndRefreshJwtToken() {
        const authenticationToken = this._authenticationToken;
        const refreshToken = this._refreshToken;
        if (refreshToken && authenticationToken && authenticationToken.length > 64 && this.getJwtExpiration()?.has_expired()) {
            try {
                // This needs to be called omitting the authentication token on purpose
                // Otherwise we'd get a 401 due to it being added
                //
                const refreshTokenResponse = await this._endpoint.callService(Core.RefreshToken, { Token: refreshToken });
                if (refreshTokenResponse?.RefreshToken && refreshTokenResponse.Token) {
                    this.updateTokens({
                        authenticationToken: refreshTokenResponse.Token,
                        refreshToken: refreshTokenResponse.RefreshToken
                    });
                }
            }
            catch (error) {
                logger.error(`[CONTEXT] Failed to refresh user token`, error);
                this._status$.next('EXPIRED');
                throw error;
            }
        }
    }
    // Can be used to set a new application, user and refresh token
    //
    updateTokens({ applicationToken, authenticationToken, refreshToken, }) {
        this._applicationToken = applicationToken ?? this._applicationToken;
        this._authenticationToken = authenticationToken ?? this._authenticationToken;
        this._refreshToken = refreshToken ?? this._refreshToken;
        if (authenticationToken) {
            this._status$.next('LOGGED-IN');
        }
        else {
            this._status$.next('LOGGED-OUT');
        }
    }
    // If a user token has expired or should be treated as such it can be provided using this method
    // If the token matches the current token the status will be updated to expired
    //
    expireToken(authenticationToken) {
        if (authenticationToken && this._authenticationToken === authenticationToken) {
            this._status$.next('EXPIRED');
        }
    }
    start() {
        // Setup an automated subscription to update the current application token
        //
        this._settingsSubscription = settings.subscribe({
            channel: `eva.setting.${SETTINGS_GROUP_NAME}.applicationTokens`,
            callback: ({ value }) => {
                logger.debug('[EVA:CONTEXT] Updated application tokens from settings', value)();
                const applicationTokens = (value.new ?? {});
                const endpointUrl = new URL(this._endpoint.endPointUri);
                const origin = endpointUrl.origin;
                this._applicationToken = applicationTokens[origin];
            }
        });
    }
    stop() {
        // Clear all subscriptions and timers
        //
        if (this._settingsSubscription) {
            settings.unsubscribe({ token: this._settingsSubscription });
            this._settingsSubscription = undefined;
        }
    }
}
//# sourceMappingURL=user.js.map